import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, CreditCard, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { openMinSupportChat } from "@/lib/api";
import { redirectToAuthIfRequired } from "@/lib/auth-required-redirect";
import { createCardUpdateLink, getBillingRecovery, resumeBillingSubscription } from "@/lib/billing-recovery-api";
import { getRecoveryResumeTerms, openCardUpdate } from "@/lib/billing-recovery";
import { useI18n } from "@/lib/i18n";

export default function BillingPaymentMethod({ accountId, subscription, action, setAction, onBillingChanged, onRecoveryChange }) {
  const { locale, tx } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [recovery, setRecovery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const readVersion = useRef(0);
  const returned = new URLSearchParams(location.search).get("card") === "return";
  const revision = [subscription?.status, subscription?.cancelAtPeriodEnd, subscription?.pendingPlanCode].join(":");

  const refresh = useCallback(async (signal) => {
    const version = ++readVersion.current;
    setLoading(true);
    setError("");
    onRecoveryChange(null);
    try {
      const payload = await getBillingRecovery(accountId, { signal });
      if (!signal?.aborted && version === readVersion.current) {
        setRecovery(payload);
        onRecoveryChange({ accountId, status: payload?.status });
      }
    } catch (cause) {
      if (signal?.aborted || version !== readVersion.current || redirectToAuthIfRequired(cause, { navigate, location })) return;
      setRecovery(null);
      setError("Could not load payment options. Try again.");
    } finally {
      if (!signal?.aborted && version === readVersion.current) setLoading(false);
    }
  }, [accountId, location, navigate, onRecoveryChange]);

  useEffect(() => {
    const controller = new AbortController();
    setRecovery(null);
    setNotice("");
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh, revision]);

  const terms = getRecoveryResumeTerms(recovery, locale);
  const failed = recovery?.status === "payment_failed";
  const busy = Boolean(action) || loading;

  async function handleCardUpdate() {
    setAction("update-card");
    setError("");
    try {
      openCardUpdate(await createCardUpdateLink(accountId));
    } catch (cause) {
      if (!redirectToAuthIfRequired(cause, { navigate, location })) setError("Could not open PayFast. Try again.");
    } finally {
      setAction("");
    }
  }

  async function handleResume(event) {
    event.preventDefault();
    if (!terms || busy) return;
    setAction("resume-billing");
    setError("");
    try {
      const result = await resumeBillingSubscription(accountId, recovery.confirmationToken);
      setNotice(["resumed", "already_active"].includes(result?.status)
        ? "Billing resumed. Paid access returns after payment is confirmed."
        : "We couldn’t confirm whether billing resumed. Refresh before trying again.");
      await refresh();
      try {
        await onBillingChanged();
      } catch (cause) {
        if (!redirectToAuthIfRequired(cause, { navigate, location })) setError("Could not load payment options. Try again.");
      }
    } catch (cause) {
      if (!redirectToAuthIfRequired(cause, { navigate, location })) {
        setError("Could not resume billing. Refresh and try again.");
        setRecovery(null);
      }
    } finally {
      setAction("");
    }
  }

  if (!loading && !error && !returned && !notice && ["none", "cancelled"].includes(recovery?.status)) return null;
  return (
    <section id="payment-method" aria-labelledby="payment-method-title" className="scroll-mt-28 rounded-3xl border bg-card p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <CreditCard className="h-5 w-5 text-primary" aria-hidden="true" />
        <h2 id="payment-method-title" className="font-display text-2xl font-semibold">{tx("Payment method")}</h2>
      </div>
      {returned && !notice ? <p className="mt-3 text-sm text-muted-foreground" role="status">{tx("Back from PayFast. Check your billing status below.")}</p> : null}
      {notice ? <p className="mt-3 text-sm" role="status">{tx(notice)}</p> : null}
      {loading ? <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground" role="status"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />{tx("Loading billing details")}</p> : null}
      {failed ? (
        <div className="mt-4 flex gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden="true" />
          <p className="text-sm font-medium">{tx("Your subscription is paused because payment failed.")}</p>
        </div>
      ) : null}
      {recovery?.canUpdateCard ? <p className="mt-3 text-sm text-muted-foreground">{tx("Update your card securely with PayFast.")}</p> : null}
      {failed && !terms && !loading ? <p className="mt-3 text-sm text-muted-foreground">{tx(recovery?.canUpdateCard ? "Update your card, then contact Min Support to resume billing." : "Reply to Min Support and we will help.")}</p> : null}
      {error || recovery?.status === "unavailable" ? <p className="mt-3 text-sm text-destructive" role="alert">{tx(error || "Could not load payment options. Try again.")}</p> : null}
      {terms ? <p id="resume-billing-terms" className="mt-3 text-sm text-muted-foreground">
        {tx(terms.timing === "immediate"
          ? "PayFast may collect your next payment of {amount} when billing resumes."
          : "Your next payment of {amount} is scheduled for {date}.").replaceAll("{amount}", terms.amount).replaceAll("{date}", terms.date)}
        {" "}{tx("Your subscription continues at {amount} per {period}.").replaceAll("{amount}", terms.amount).replaceAll("{period}", tx(terms.period))}
      </p> : null}
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        {recovery?.canUpdateCard ? (
          <Button className="h-11 rounded-xl" variant={returned && terms ? "outline" : "default"} disabled={busy} onClick={handleCardUpdate}>
            {action === "update-card" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}{tx("Update card")}
          </Button>
        ) : null}
        {terms ? <Button className="h-11 rounded-xl" variant={returned ? "default" : "outline"} disabled={busy} aria-describedby="resume-billing-terms" onClick={handleResume}>{action === "resume-billing" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}{tx("Resume billing")}</Button> : null}
        {error || returned || notice || recovery?.status === "unavailable" ? <Button className="h-11 rounded-xl" variant="outline" disabled={busy} onClick={() => void refresh()}>{tx("Refresh billing status")}</Button> : null}
        {!loading && (failed || recovery?.status === "unavailable" || error) ? <Button className="h-11 rounded-xl" variant="ghost" disabled={busy} onClick={() => void openMinSupportChat("billing_recovery")}>{tx("Message Min Support")}</Button> : null}
      </div>
    </section>
  );
}
